import { Loader2 } from "lucide-react";

export const Card = ({ children, className = "", testId, onClick }) => (
  <div data-testid={testId} onClick={onClick} className={`bg-card text-card-foreground rounded-2xl border border-border shadow-sm p-5 ${className}`}>
    {children}
  </div>
);

export const PageTitle = ({ children, action, testId }) => (
  <div className="flex items-center justify-between gap-3 flex-wrap">
    <h1 data-testid={testId} className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-foreground">{children}</h1>
    {action}
  </div>
);

export const Btn = ({ children, variant = "primary", className = "", busy, testId, ...rest }) => {
  const styles = {
    primary: "bg-primary text-primary-foreground hover:bg-[hsl(121,88%,42%)] hover:shadow-[0_0_20px_rgba(15,232,20,0.3)]",
    outline: "border border-border text-foreground hover:bg-secondary",
    danger: "border border-destructive/40 text-destructive hover:bg-destructive/10",
    ghost: "text-muted-foreground hover:bg-secondary hover:text-foreground",
  };
  return (
    <button
      data-testid={testId}
      disabled={busy || rest.disabled}
      {...rest}
      className={`inline-flex items-center justify-center gap-2 h-11 px-5 rounded-full text-sm font-bold active:scale-95 transition-[background-color,transform,box-shadow] disabled:opacity-50 disabled:cursor-not-allowed ${styles[variant]} ${className}`}
    >
      {busy && <Loader2 className="w-4 h-4 animate-spin" />}
      {children}
    </button>
  );
};

export const Field = ({ label, children, hint }) => (
  <label className="block">
    <span className="block text-sm font-medium text-foreground mb-1.5">{label}</span>
    {children}
    {hint && <span className="block text-xs text-muted-foreground mt-1">{hint}</span>}
  </label>
);

export const inputCls =
  "w-full h-12 rounded-xl border border-input bg-background text-foreground px-4 text-base placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring";

export const Input = ({ testId, className = "", ...rest }) => <input data-testid={testId} {...rest} className={`${inputCls} ${className}`} />;

export const Select = ({ testId, className = "", children, ...rest }) => (
  <select data-testid={testId} {...rest} className={`${inputCls} ${className}`}>{children}</select>
);

const STATUS_STYLES = {
  paid: "bg-primary/15 text-[hsl(122,87%,28%)] dark:text-primary",
  active: "bg-primary/15 text-[hsl(122,87%,28%)] dark:text-primary",
  approved: "bg-primary/15 text-[hsl(122,87%,28%)] dark:text-primary",
  pending: "bg-amber-500/15 text-amber-700 dark:text-amber-400",
  requested: "bg-amber-500/15 text-amber-700 dark:text-amber-400",
  created: "bg-secondary text-secondary-foreground",
  rejected: "bg-destructive/15 text-destructive",
  suspended: "bg-destructive/15 text-destructive",
  locked: "bg-orange-500/15 text-orange-700 dark:text-orange-400",
  approved: "bg-primary/15 text-[hsl(122,87%,28%)] dark:text-primary",
  expired: "bg-secondary text-muted-foreground",
  voided: "bg-secondary text-muted-foreground",
  disabled: "bg-secondary text-muted-foreground",
  refunded: "bg-sky-500/15 text-sky-700 dark:text-sky-400",
  chargeback: "bg-destructive/15 text-destructive",
  open: "bg-amber-500/15 text-amber-700 dark:text-amber-400",
  under_review: "bg-sky-500/15 text-sky-700 dark:text-sky-400",
  won: "bg-primary/15 text-[hsl(122,87%,28%)] dark:text-primary",
  lost: "bg-destructive/15 text-destructive",
};

export const Badge = ({ status, label, testId }) => (
  <span data-testid={testId} className={`inline-flex text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap ${STATUS_STYLES[status] || STATUS_STYLES.created}`}>
    {label || status}
  </span>
);

export const Spinner = ({ testId }) => <Loader2 data-testid={testId} className="w-6 h-6 animate-spin text-primary" />;

export const Empty = ({ children, testId }) => (
  <Card testId={testId} className="text-sm text-muted-foreground text-center py-10">{children}</Card>
);

export const Modal = ({ title, onClose, children, testId }) => (
  <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center" data-testid={testId}>
    <div className="w-full max-h-full sm:max-w-lg bg-card text-card-foreground sm:rounded-2xl rounded-t-2xl border border-border p-6 flex flex-col gap-4 overflow-y-auto">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-lg font-bold">{title}</h2>
        <button type="button" onClick={onClose} data-testid={`${testId}-close`} className="w-11 h-11 flex items-center justify-center rounded-xl text-muted-foreground hover:bg-secondary transition-colors" aria-label="close">✕</button>
      </div>
      {children}
    </div>
  </div>
);

export const fmtMoney = (v) => `$${Number(v || 0).toFixed(2)}`;
export const fmtDate = (iso, lang) => (iso ? new Date(iso).toLocaleString(lang === "es" ? "es-PA" : "en-US", { dateStyle: "short", timeStyle: "short" }) : "—");
